/**
 * ============================================
 * CUSTOMERS API SERVICE
 * ============================================
 * Complete service for customer and address management
 */

import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  }
});

// Add auth token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

export const customersAPI = {
  /**
   * Get all customers with search/filter
   * ✅ Fetches name, email, phone from customers table
   */
  getAll: async (params = {}) => {
    try {
      console.log('📥 Fetching customers with params:', params);
      const response = await axiosInstance.get('/customers', { params });
      
      const customers = response.data?.data?.customers || response.data?.customers || [];
      console.log(`✅ Retrieved ${customers.length} customers`);
      
      return {
        success: response.data?.success || true,
        data: {
          customers: customers
        }
      };
    } catch (error) {
      console.error('❌ Failed to fetch customers:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get single customer with all addresses
   * ✅ Returns customer details + addresses from customer_addresses table
   */
 /**
 * Get single customer with all addresses
 * ✅ FIXED: Uses /manage/:customerId route
 */
getById: async (customerId) => {
  try {
    console.log(`📥 Fetching customer: ${customerId}`);
    // ✅ CHANGED: /customers/:id → /customers/manage/:id
    const response = await axiosInstance.get(`/customers/manage/${customerId}`);
    
    const customer = response.data?.data?.customerId 
      ? {
          id: response.data.data.customerId,
          name: response.data.data.name,
          email: response.data.data.email,
          phone: response.data.data.phone,
          address: response.data.data.serviceAddress?.address || '',
          city: response.data.data.serviceAddress?.city || '',
          state: response.data.data.serviceAddress?.state || '',
          pincode: response.data.data.serviceAddress?.pincode || ''
        }
      : null;
    
    // ✅ FIXED: Use allAddresses (includes billing, shipping, service) instead of addresses
    const addresses = response.data?.data?.allAddresses || response.data?.data?.addresses || [];
    
    console.log(`✅ Customer loaded: ${customer?.name}`);
    console.log(`📍 Addresses found: ${addresses.length}`);
    
    return {
      success: response.data?.success || true,
      data: {
        customer: customer,
        addresses: addresses
      }
    };
  } catch (error) {
    console.error(`❌ Failed to fetch customer:`, error.response?.data || error.message);
    throw error.response?.data || error;
  }
},

  /**
   * Get customer addresses
   * ✅ Fetches address_type, address, city, state, pincode from customer_addresses
   */
  getAddresses: async (customerId, params = {}) => {
    try {
      console.log(`📥 Fetching addresses for customer: ${customerId}`);
      const response = await axiosInstance.get(`/customers/${customerId}/addresses`, { params });
      
      const addresses = response.data?.data?.addresses || response.data?.addresses || [];
      console.log(`✅ Retrieved ${addresses.length} addresses`);
      
      return {
        success: response.data?.success || true,
        data: {
          addresses: addresses
        }
      };
    } catch (error) {
      console.error(`❌ Failed to fetch addresses:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Search customers by name, phone, or email
   */
  search: async (searchQuery) => {
    try {
      console.log(`🔍 Searching customers: ${searchQuery}`);
      const response = await axiosInstance.get('/customers/search', {
        params: { q: searchQuery }
      });
      
      const customers = response.data?.data?.customers || [];
      console.log(`✅ Found ${customers.length} matching customers`);
      
      return {
        success: response.data?.success || true,
        data: {
          customers: customers
        }
      };
    } catch (error) {
      console.error('❌ Search failed:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Create new customer
   */
  create: async (customerData) => {
    try {
      console.log('📝 Creating customer:', customerData);
      const response = await axiosInstance.post('/customers', customerData);
      
      const customer = response.data?.data?.customer;
      console.log(`✅ Customer created: ${customer?.name}`);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create customer:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Create or link customer (for quotations)
   * - Checks if customer exists by phone/email
   * - If exists: Links to current client
   * - If not: Creates new customer and links to client
   */
  createOrLink: async (customerData) => {
    try {
      console.log('🔗 Create or link customer:', customerData);
      const response = await axiosInstance.post('/customers/create-or-link', customerData);
      
      const customer = response.data?.data?.customer;
      const isNewCustomer = response.data?.data?.isNewCustomer;
      
      console.log(`✅ ${isNewCustomer ? 'Created new' : 'Linked existing'} customer: ${customer?.name}`);
      
      return {
        success: true,
        data: {
          customer,
          isNewCustomer
        },
        message: response.data?.message
      };
    } catch (error) {
      console.error('❌ Failed to create or link customer:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Check if customer exists by email or phone
   * Returns customer data if found, null if not
   */
  checkExists: async (email, phone) => {
    try {
      console.log('🔍 Checking if customer exists:', { email, phone });
      const response = await axiosInstance.post('/customers/check-exists', { email, phone });
      
      const exists = response.data?.data?.exists;
      const customer = response.data?.data?.customer;
      const isAlreadyLinked = response.data?.data?.isAlreadyLinked || false;
      
      if (exists) {
        console.log(`✅ Customer exists: ${customer?.name}${isAlreadyLinked ? ' (already linked)' : ''}`);
      } else {
        console.log('❌ Customer not found');
      }
      
      return {
        success: true,
        exists,
        customer,
        isAlreadyLinked
      };
    } catch (error) {
      console.error('❌ Failed to check customer existence:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Update customer
   */
  update: async (customerId, customerData) => {
    try {
      console.log(`📝 Updating customer ${customerId}:`, customerData);
      const response = await axiosInstance.put(`/customers/${customerId}`, customerData);
      
      console.log(`✅ Customer updated`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update customer:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  }
};

export default customersAPI;